import React, { useEffect, useState } from 'react';
import { Table, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface ItemData {
  id: string | number;
  code: string;
  name: string;
  type: string;
  importEarnings?: number;
  exportEarnings?: number;
}

interface CategoryData {
  id: string | number;
  code: string;
  name: string;
  importEarnings?: number;
  exportEarnings?: number;
  items?: ItemData[];
}

interface DepartmentData {
  id: string | number;
  code: string;
  name: string;
  importEarnings?: number;
  exportEarnings?: number;
  categories?: CategoryData[];
}

interface ImportExportNominalEarningsProps {
  reportKey: string;
  filters: any;
}

const ImportExportNominalEarnings: React.FC<ImportExportNominalEarningsProps> = ({ reportKey, filters }) => {
  const [data, setData] = useState<DepartmentData[]>([]);
  const [loading, setLoading] = useState(false);

  const columns = [
    {
      title: 'Department / Category / Item',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Earnings (LKR)',
      dataIndex: 'importEarnings',
      key: 'importEarnings',
      render: (value: number | undefined) =>
        value !== undefined ? value.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : '-',
    },
    // {
    //   title: 'Export Earnings (LKR)',
    //   dataIndex: 'exportEarnings',
    //   key: 'exportEarnings',
    //   render: (value: number | undefined) =>
    //     value !== undefined ? value.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : '-',
    // },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!reportKey || !filters) return;

      const reportApi = getReportApiByKey(reportKey);
      if (!reportApi) {
        message.error('Invalid report key.');
        return;
      }

      setLoading(true);
      try {
        const response = await reportApi.generateReport(filters);

        // Transform the nested report structure: Department → Categories → Items
        const departments: DepartmentData[] = Object.values(response?.report || {}).map((dept: any) => ({
          id: dept.id,
          code: dept.code,
          name: `${dept.code} - ${dept.name}`,
          importEarnings: dept.total,
          exportEarnings: undefined,
          categories: Object.values(dept.categories || {}).map((category: any) => ({
            id: category.id,
            code: category.code,
            name: `${category.code} - ${category.name}`,
            importEarnings: category.total,
            exportEarnings: undefined,
            items: Object.values(category.items || {}).map((item: any) => ({
              id: item.id,
              code: item.code,
              name: item.name,
              type: item.type,
              importEarnings: item.total,
              exportEarnings: undefined,
            })),
          })),
        }));

        setData(departments);
      } catch (err) {
        message.error('Failed to fetch Import & Export Nominal Earnings report data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters, reportKey]);

  const tableData = data.map((dept) => ({
    key: `dept-${dept.id}`,
    name: dept.name,
    importEarnings: dept.importEarnings,
    exportEarnings: dept.exportEarnings,
    children: dept.categories?.map((category) => ({
      key: `dept-${dept.id}-cat-${category.id}`,
      name: `${category.name}`,
      importEarnings: category.importEarnings,
      exportEarnings: category.exportEarnings,
      children: category.items?.map((item) => ({
        key: `dept-${dept.id}-cat-${category.id}-item-${item.id}`,
        name: `${item.code} - ${item.name} (${item.type})`,
        importEarnings: item.importEarnings,
        exportEarnings: item.exportEarnings,
      })),
    })),
  }));

  return (
    <Table
      columns={columns}
      dataSource={tableData}
      pagination={false}
      expandable={{ defaultExpandAllRows: true }}
      bordered
      loading={loading}
    />
  );
};

export default ImportExportNominalEarnings;
